import { SmartFilters, SmartForm, SmartTable } from '@certemy/ui-components';
import { PROFILE_TYPE_ID } from 'components/organization-users/users/users.constants';
import { GlobalOrganizationsLabel } from 'features/global-organizations-setting-state/global-organizations-setting.constant';
import { TemplateRef } from '@angular/core';
import { DependencyModal } from 'features/dependency-modal/dependency-modal.constant';
import REGISTRY_DEPENDENCY_TYPE = DependencyModal.REGISTRY_DEPENDENCY_TYPE;

const ColumnTypeId = SmartTable.ColumnTypeId;

export interface RegistryDependencyRow {
  registryDependencyId: number;
  firstName: string;
  lastName: string;
  profileType: PROFILE_TYPE_ID;
  credentialName: string;
  dependencyName: string;
  startDate: string;
  endDate: string;
  dependencyTypeId: DependencyModal.REGISTRY_DEPENDENCY_TYPE;
  statusId: DependencyModal.DEPENDENCY_STATUS;
  profileId: number;
  acronym: string;
  selected?: boolean;
}

export interface RegistryDependencyTableData {
  rows: RegistryDependencyRow[];
  count: number;
}

export const START_DATE_COLUMN = {
  name: 'Start date',
  value: 'startDate',
  type: ColumnTypeId.DATE,
  sortable: true,
};

export const getRegistryDependencyTableHeaders = (
  labels: GlobalOrganizationsLabel,
  iconColumnValueTemplateRef: TemplateRef<any>,
  canEdit: boolean,
): SmartTable.Header[] => {
  return [
    ...(canEdit
      ? [
          {
            name: '',
            value: 'selected',
            type: ColumnTypeId.CHECK,
          },
        ]
      : []),
    {
      name: 'First Name',
      value: 'firstName',
      type: ColumnTypeId.STRING,
      sortable: true,
    },
    {
      name: 'Last Name',
      value: 'lastName',
      type: ColumnTypeId.STRING,
      sortable: true,
    },
    {
      name: 'Profile Type',
      value: 'profileType',
      type: ColumnTypeId.STRING,
      sortable: true,
    },
    {
      name: 'Credential Name',
      value: 'credentialName',
      type: ColumnTypeId.STRING,
      sortable: true,
    },
    {
      name: 'Dependency Name',
      value: 'dependencyName',
      type: ColumnTypeId.STRING,
      sortable: true,
    },
    START_DATE_COLUMN,
    {
      name: 'End date',
      value: 'endDate',
      type: ColumnTypeId.DATE,
      sortable: true,
    },
    {
      name: 'Dependency Type',
      type: ColumnTypeId.CUSTOM,
      value: row => DependencyModal.REGISTRY_DEPENDENCY_TYPE_NAMES[row.dependencyTypeId],
      propertyName: 'dependencyTypeId',
      sortable: true,
    },
    {
      name: 'Dependency Status',
      type: ColumnTypeId.CUSTOM,
      width: 110,
      maxWidth: 110,
      value: row => DependencyModal.DEPENDENCY_STATUS_NAMES[row.statusId],
      propertyName: 'statusId',
      sortable: true,
    },
    {
      name: 'Dependency Modal',
      type: ColumnTypeId.TEMPLATE,
      width: 100,
      maxWidth: 100,
      value: iconColumnValueTemplateRef,
    },
  ];
};

export class RegistryDependencyDefaultFilters {
  search = '';
  order = {
    header: START_DATE_COLUMN,
    order: SmartTable.Order.DESC,
  };
  filters: RegistryDependencySmartFiltersValues = {
    workflowSubtypeId: [],
    credentialNameId: [],
    workflowNameId: [],
    countryId: [],
    regionId: [],
    officeId: [],
    profileTypeId: null,
    dependencyStatus: [DependencyModal.DEPENDENCY_STATUS.ACTIVE],
  };
  page = 1;
  page_size = 25;
}

export interface RegistryDependencyFilters {
  search: string;
  page: number;
  page_size: number;
  filters: RegistryDependencySmartFiltersValues;
  order: SmartTable.OrderedHeader;
  force?: boolean;
}

export interface RegistryDependencyQueryFilters {
  search: string;
  page: number;
  page_size: number;
  filters: RegistryDependencySmartFiltersValues;
  orderId: string;
  orderType: SmartTable.Order;
}

export interface RegistryDependencySmartFiltersValues {
  workflowSubtypeId?: number[];
  credentialNameId?: number[];
  workflowNameId?: number[];
  countryId?: number[];
  regionId?: number[];
  officeId?: number[];
  profileTypeId?: PROFILE_TYPE_ID;
  dependencyStatus?: number[];
}

export const REGISTRY_DEPENDENCY_PAGE_SIZE_ITEMS = [10, 25, 50, 100];

export const REGISTRY_DEPENDENCY_TEXTS = {
  DEACTIVATE_WARNING:
    'Once you inactivate a dependency, you cannot re-activate it. You will need to create a new dependency. This is to ensure we have an accurate history of the start and end date for each dependency',
  DEPENDENCY_NAME_TOOLTIP: 'Please provide a name for this dependency. This name will show up in reports',
  START_DATE_TOOLTIP:
    'Please select a start date for when this dependency began. This will be used for reporting purposes',
  DEPENDENT_TOOLTIP: 'Please select the credential holder that “depends on” another credential holder',
  DEPENDENT_REGISTRY_RECORD_TOOLTIP:
    'Please select the registry record (credential) that “depends on” another registry record (credential.)',
  INDEPENDENT_TOOLTIP:
    'Please select the credential holder that the previously selected credential holder is “dependent upon"',
  INDEPENDENT_REGISTRY_RECORD_TOOLTIP:
    'Please select the registry record (credential) that the previously selected registry record (credential) is “dependent upon“',
  DEPENDENCY_TYPE_TOOLTIP:
    'If the independent registry record you select must be in good standing for the dependent registry record to be in good standing, please select “required.” If the independent registry record you select is only one of several registry records that must be in good standing for the dependent registry record to be in good standing, please select “one of.”',
  CREATE_WARNING:
    'Are you sure you want to create this registry dependency? Once you do, you cannot edit it. You will only be able to deactivate it',
  REGISTRY_RECORD_PART_OF_EXISTING:
    'This registry dependency already exists. Please inactivate the existing dependency first or select a different dependency',
  RECORD_HAS_ACTIVE_REQUIRED_DEPENDENCY:
    'The dependent registry record already has an existing required dependency. Are you sure you wish to proceed with this new dependency?',
  RECORD_HAS_ACTIVE_ONE_OF_DEPENDENCY:
    'The dependent registry record already has an existing “one of” dependency. Please inactivate the existing dependency first before creating a new one. This is necessary in order to keep track of when each dependency was active',
  RECORDS_ARE_THE_SAME:
    'The dependent and independent registry records are the same. Please select a different independent registry record',
  ONLY_ONE_INDEPENDENT_SELECTED:
    'You have indicated you wish to add a “one of” dependency” but you have selected only one independent registry record. Please add at least one more independent registry record or change the dependency type to “required',
};

export enum REGISTRY_DEPENDENCY_FIELD {
  DEPENDENCY_NAME,
  DEPENDENT,
  DEPENDENT_REGISTRY_RECORD,
  INDEPENDENT,
  INDEPENDENT_REGISTRY_RECORD,
}

export const REGISTRY_DEPENDENCY_TYPE_OPTIONS: SmartForm.FieldOption[] = Object.keys(
  DependencyModal.REGISTRY_DEPENDENCY_TYPE_NAMES,
).map(key => ({ id: Number(key), name: DependencyModal.REGISTRY_DEPENDENCY_TYPE_NAMES[key] }));

export enum REGISTRY_DEPENDENCY_FILTERS {
  CREDENTIAL_TYPE = 1,
  WORKFLOW_NAME = 2,
  CREDENTIAL_NAME = 3,
  ENTITY_TYPE = 4,
  STATUS = 5,
}

export interface RegistryDependencyPotentialProfile {
  firstName: string;
  lastName: string;
  profileId: number;
}

export interface RegistryDependencyPotentialCertifications {
  acronym: string;
  credentialName: string;
  certificationNumberId: number;
}

export interface RegistryDependencyNames {
  dependencyName: string;
}

const RegistryDependencyStatusNames = {
  [DependencyModal.DEPENDENCY_STATUS.ACTIVE]: 'ACTIVE',
  [DependencyModal.DEPENDENCY_STATUS.INACTIVE]: 'INACTIVE',
};

export const DependencyStatusDefaultValue: SmartFilters.FilterOption = {
  id: DependencyModal.DEPENDENCY_STATUS.ACTIVE,
  name: RegistryDependencyStatusNames[DependencyModal.DEPENDENCY_STATUS.ACTIVE],
};

export const DependencyStatusDefaultOptions: SmartFilters.FilterOption[] = [
  {
    id: DependencyModal.DEPENDENCY_STATUS.ACTIVE,
    name: RegistryDependencyStatusNames[DependencyModal.DEPENDENCY_STATUS.ACTIVE],
  },
  {
    id: DependencyModal.DEPENDENCY_STATUS.INACTIVE,
    name: RegistryDependencyStatusNames[DependencyModal.DEPENDENCY_STATUS.INACTIVE],
  },
];

export interface RegistryDependencyCreatePayload {
  dependentProfileId: number;
  dependentAcronym: string;
  independents: RegistryDependencyIndependent[];
  dependencyTypeId: REGISTRY_DEPENDENCY_TYPE;
  dependencyName: string;
  startDate: string;
}

interface RegistryDependencyIndependent {
  independentProfileId: number;
  independentAcronym: string;
}

export interface RegistryDependencyValidationResponse {
  message: string;
  level: string;
}
